import { EditIcon } from '@chakra-ui/icons';
import { Button, Box, Flex, Heading, VStack, Icon } from '@chakra-ui/react';
import { Link } from 'react-scroll';
import {
  HotelFragmentFragment,
  RatingConnection,
} from '../../../generated/types';
import useHotelMapMarkers from '../../../hooks/useHotelMapMarkers';
import { useStore } from '../../../store/provider';
import renderTeaser from '../../../utils/render-teaser';
import { HTMLText } from '../../Common/HTMLText';
import PageImagesEbikeAtlas, {
  SliderImage,
} from '../../Common/PageImagesEbikeAtlas';
import ContactPopoverButton from '../../Node/Hotel/ContactPopoverButton';
import { CTAStack } from '../../Node/Hotel/EbikeAtlasHotelIndex';
import FeatureList from '../../Node/Hotel/FeatureList';
import HotelRatingList from '../../Node/Hotel/HotelRatingList';
import HotelTestimonial from '../../Node/Hotel/HotelTestimonial';
import RatingStars from '../../Node/Hotel/RatingStars';
import dynamic from 'next/dynamic';
import Container from '../../Layout/Container';
import useMergedHotelFeatures from '../../../hooks/useMergedHotelFeatures';
import { FaExternalLinkAlt } from '@react-icons/all-files/fa/FaExternalLinkAlt';

const HotelMap = dynamic(() => import('../../Common/HotelMap'), {
  ssr: false,
});

interface HotelDetailProps {
  data: HotelFragmentFragment;
  setShowRatingModal: (val: boolean) => void;
  setShowBookingModal: (val: boolean) => void;
}

const HotelContent: React.FC<HotelDetailProps> = ({
  data,
  setShowRatingModal,
  setShowBookingModal,
}) => {
  const store = useStore();

  const images: SliderImage[] = [data.teaserImage] as SliderImage[];
  const mapMarkers = useHotelMapMarkers(data);

  if (data.detailsForFrontend && data.detailsForFrontend.images) {
    data.detailsForFrontend.images.forEach((image: any) => {
      images.push(image);
    });
  }

  const activities = data.activities?.filter(
    (activity) => activity.category.key === 'ebike'
  );

  const features = useMergedHotelFeatures(data);

  return (
    <Box>
      <Container paddingY={4}>
        <CTAStack
          setShowRatingModal={setShowRatingModal}
          setShowBookingModal={setShowBookingModal}
          data={data}
          display={{
            base: 'flex',
            md: 'none',
          }}
          mb={{
            base: 8,
            md: 0,
          }}
        />

        <Flex
          flexDirection={{
            base: 'column',
            md: 'column',
            lg: 'row',
          }}
        >
          <Box flex={1}>
            <Heading>{data.title}</Heading>
            {data.detailsForFrontend && (
              <Box mt={4} fontSize="lg">
                <HTMLText text={data.detailsForFrontend?.body} />
              </Box>
            )}

            {features.length > 0 && (
              <Box mt={8}>
                <Heading mb={8}>Ausstattung</Heading>

                <FeatureList features={features} columns={2} buttonLeft />
              </Box>
            )}

            <Flex
              flex={1}
              mt={8}
              flexDirection={{
                base: 'column',
                md: 'row',
              }}
            >
              <Box flex={1}>
                <Heading mb={8}>Kontakt</Heading>
                <VStack spacing={4}>
                  <ContactPopoverButton {...data} />
                  {!data.onlyShowOnlineBooking && (
                    <Button
                      width="100%"
                      onClick={() => setShowBookingModal(true)}
                    >
                      Buchungsanfrage
                    </Button>
                  )}
                  {data.onlineBookingUrl && (
                    <Button
                      width="100%"
                      as="a"
                      href={data.onlineBookingUrl}
                      target="_blank"
                    >
                      Online buchen <Icon ml={2} as={FaExternalLinkAlt} />
                    </Button>
                  )}
                </VStack>
              </Box>
              <Box
                marginLeft={{
                  base: 0,
                  md: 8,
                }}
                mt={{
                  base: 8,
                  md: 0,
                }}
                flex={1}
              >
                <Heading mb={8}>Bewertungen</Heading>
                <RatingStars
                  title="Verpflegung"
                  value={data.averageRating!.catering}
                />
                <RatingStars
                  title="Unterkunft"
                  value={data.averageRating!.housing}
                />
                <RatingStars
                  title="Freundlichkeit"
                  value={data.averageRating!.kindness}
                />
                <Link to="bewertungen" smooth>
                  <Button mt={4} width="100%">
                    Bewertungen ansehen
                  </Button>
                </Link>
              </Box>
            </Flex>
          </Box>
          {activities?.length > 0 && (
            <Box
              flex="0 0 auto"
              width={{
                lg: '500px',
              }}
              mt={{
                base: 12,
                md: 0,
              }}
              ml={{
                base: 0,
                md: 12,
              }}
            >
              <Heading mb={8}>{store.config.titles?.hotel?.activities}</Heading>
              {renderTeaser(activities[0], {
                vertical: true,
              })}

              {activities?.length > 1 && (
                <Box
                  pt={4}
                  mt={4}
                  borderTop="1px solid"
                  borderTopColor="gray.200"
                >
                  <Link to="further-activities" smooth>
                    <Button mt={2} width="100%">
                      Weitere Touren
                    </Button>
                  </Link>
                </Box>
              )}
            </Box>
          )}
        </Flex>
      </Container>

      {images.length > 0 && (
        <Box mt={12}>
          <Container>
            <Heading>Fotos</Heading>

            <PageImagesEbikeAtlas images={images} />
          </Container>
        </Box>
      )}

      {data.location && (
        <Box mt={12}>
          <HotelMap
            scrollWheelZoomDelayed
            scrollWheelZoom={false}
            data={data}
            markers={mapMarkers}
            center={[data.location!.lat, data.location!.lng]}
          />
        </Box>
      )}

      {data.pushedRatings?.total > 0 && (
        <Box mt={12}>
          <HotelTestimonial
            image={data.testimonialImage || data.teaserImage}
            rating={data.pushedRatings.items?.[0]}
            darken={!data.testimonialImage}
            showCallToAction={data.ratings?.total > 1}
          />
        </Box>
      )}

      {activities?.length > 1 && (
        <>
          <Container small id="further-activities">
            <Box mt={12}>
              <Heading mb={8}>
                {store.config.titles?.hotel?.furterActivities}
              </Heading>
            </Box>
          </Container>
          <Container>
            <Box my={8}>
              <VStack spacing={8}>
                {activities.slice(1).map((t) => renderTeaser(t, {}))}
              </VStack>
            </Box>
          </Container>
        </>
      )}

      <Box mt={12} id="bewertungen">
        <Container smaller>
          <Flex
            alignItems="center"
            justifyContent="space-between"
            direction={{
              base: 'column',
              md: 'row',
            }}
          >
            <Heading>Bewertungen</Heading>

            <Box>
              <Button onClick={() => setShowRatingModal(true)}>
                <EditIcon marginRight={2} />
                Bewertung schreiben
              </Button>
            </Box>
          </Flex>

          <HotelRatingList
            hotel={data}
            data={data.ratings! as RatingConnection}
          />
        </Container>
      </Box>
    </Box>
  );
};

export default HotelContent;
